import {DataTestAttrs, getTestProps} from '@benchling/js-test-utils';
import {packSx, Stack, type StackProps, MantineTheme} from '@mantine/core';
import React, {type PropsWithChildren, useMemo} from 'react';

import {ReactFlowLockedContext} from 'ui-library/reactflow/LockedContext';
import {Header} from 'ui-library/reactflow/nodes/BenchlingNode/v2/BenchlingNodeHeader';
import {
  Section,
  Sections,
  SectionRow,
} from 'ui-library/reactflow/nodes/BenchlingNode/v2/BenchlingNodeSection';
import {
  SectionsSummary,
  Summary,
  SummaryItem,
} from 'ui-library/reactflow/nodes/BenchlingNode/v2/BenchlingNodeSummary';
import {BenchlingNodeV2Status} from 'ui-library/reactflow/nodes/BenchlingNode/v2/BenchlingNodeV2Constants';
import {StandardBenchlingNode} from 'ui-library/reactflow/nodes/BenchlingNode/v2/standard';
import {
  BenchlingNodeV2Context,
  BenchlingNodeV2ContextValue,
} from 'ui-library/reactflow/nodes/BenchlingNode/v2/useBenchlingNodeV2Context';

export interface BenchlingNodeV2Props {
  /**
   * The status of the node. When provided, this will set a background color and an icon for the node header
   * corresponding with that status (e.g. yellow background and a triangle-exclamation icon for 'warning')
   */
  status?: BenchlingNodeV2Status;
  /**
   * A description of the node's status. When provided, this will be shown in a tooltip on the status icon
   * in the node's header.
   */
  statusDescription?: string | JSX.Element;
  /**
   * Whether the node is disabled. When true, the header background will be gray and a lock icon
   * will be shown in the header.
   * This is separate from the locked state - a node can be disabled for node-specific reasons.
   */
  isDisabled?: boolean;

  /**
   * Whether the node is selected. When true, this will make the node's border blue.
   */
  isSelected?: boolean;

  /**
   * Whether or not the node can be selected. When true, the node will have a blue border when selected and
   * will have additional styling applied when hovered and active.
   *
   * Defaults to true.
   */
  isSelectable?: boolean;

  dataTestAttrs?: Omit<DataTestAttrs, 'component'>;
}

/**
 * Wrapper component for rendering a flowchart node.
 *
 * This component is a context provider. As such, its namespaced subcomponents automatically handle common
 * flowchart node concerns such as node status, disabled status, and section row count.
 */
export function BenchlingNodeV2(props: PropsWithChildren<BenchlingNodeV2Props & StackProps>): JSX.Element {
  const {
    status,
    statusDescription,
    isDisabled = false,
    sx,
    isSelected = false,
    isSelectable = true,
    dataTestAttrs,
    w = '200px',
    ...containerProps
  } = props;

  const isLocked = ReactFlowLockedContext.useContext() ?? false;

  // For rendering purposes, we treat the node as disabled if either isDisabled OR isLocked is true
  const shouldRenderAsDisabled = isDisabled || isLocked;

  const contextValue = useMemo(
    (): BenchlingNodeV2ContextValue => ({
      status,
      statusDescription,
      isDisabled: shouldRenderAsDisabled,
    }),
    [status, statusDescription, shouldRenderAsDisabled]
  );

  const getBorderColor = (
    theme: MantineTheme
  ): {borderColor: string; hoverBorderColor: string; hoverActiveBorderColor: string} => {
    if (isSelectable && isSelected) {
      const borderColor = theme.colors.blue[7];
      return {borderColor, hoverBorderColor: borderColor, hoverActiveBorderColor: borderColor};
    }
    return {
      borderColor: theme.colors.gray[2],
      hoverBorderColor: theme.colors.blue[4],
      hoverActiveBorderColor: theme.colors.blue[4],
    };
  };

  return (
    <BenchlingNodeV2Context.Provider value={contextValue}>
      <Stack
        spacing="0px"
        w={w}
        sx={[
          (theme) => {
            const {borderColor, hoverBorderColor, hoverActiveBorderColor} = getBorderColor(theme);
            return {
              backgroundColor: theme.white,
              borderRadius: theme.radius.md,
              border: `1px solid ${borderColor}`,
              userSelect: 'none',
              ...(isSelectable && !shouldRenderAsDisabled
                ? {
                    '&:hover': {
                      boxShadow: '0px 2px 6px 0px rgba(186, 186, 189, 0.75)',
                      borderColor: hoverBorderColor,
                      ':active': {
                        borderColor: hoverActiveBorderColor,
                      },
                    },
                  }
                : {}),
            };
          },
          ...packSx(sx),
        ]}
        {...containerProps}
        {...getTestProps({component: 'BenchlingNodeV2', ...(dataTestAttrs ?? {})})}
      >
        {props.children}
      </Stack>
    </BenchlingNodeV2Context.Provider>
  );
}

/**
 * A lightweight header for a {@link BenchlingNodeV2}. This is a flex column with BenchlingNode styling
 * applied.
 * It consumes the context provided by {@link BenchlingNodeV2}, and as such automatically handles node status
 * and disabled status.
 * @memberof BenchlingNodeV2
 */
BenchlingNodeV2.Header = Header;
/**
 * A styled flex row wrapper around one or more {@link SummaryItem}.
 */
BenchlingNodeV2.Summary = Summary;
/**
 * Renders a count of items for a section, keyed by that section's icon.
 */
BenchlingNodeV2.SummaryItem = SummaryItem;

/**
 * A wrapper for one or more {@link BenchlingNodeV2.Section}.
 *
 * @param props.showSummary Whether to display a {@link SectionsSummary} at the top.
 */
BenchlingNodeV2.Sections = Sections;
/**
 * Renders a count of items for each section, keyed by that section's icon.
 */
BenchlingNodeV2.SectionsSummary = SectionsSummary;
/**
 * A section within a {@link BenchlingNodeV2.Sections}. This is a flex column with BenchlingNode styling
 * applied.
 * @param props.name The name of the section. This is displayed in black text at the top of the section.
 */
BenchlingNodeV2.Section = Section;
/**
 * A row within a {@link BenchlingNodeV2.Section}. This is a flex row with BenchlingNode styling applied.
 */
BenchlingNodeV2.SectionRow = SectionRow;
/**
 * Standard versions of {@link BenchlingNodeV2} subcomponents. These standard subcomponents handle
 * commonly-used styles, icons, etc.
 */
BenchlingNodeV2.Standard = StandardBenchlingNode;
